import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  BellRing,
  Check,
  ChevronDown,
  Clock,
  Minus,
  Settings2,
  Sparkles,
  X
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';

import { anomalyToExplainState, useChatInteract } from '@chainlit/react-client';

import AlertsDialog from '@/components/anomalies/AlertsDialog';
import RulesManager from '@/components/anomalies/RulesManager';
import WeeklyDigest from '@/components/anomalies/WeeklyDigest';
import {
  Anomaly,
  AnomalySeverity,
  AnomalyStatus,
  AnomalySummary,
  Timeframe,
  apiAnomalySummary,
  apiGetAnomaly,
  apiListAnomalies,
  apiPatchAnomaly
} from '@/components/anomalies/api';
import {
  absoluteTime,
  comparedWith,
  comparisonLabel,
  comparisonNotes,
  driverOffsetByOthers,
  driverShareIsTrustworthy,
  formatPct,
  formatValue,
  groupByImpact,
  humanizeName,
  impactBand,
  impactBandLabel,
  impactScaleKnown,
  impactText,
  isYearOverYear,
  isoPeriodRange,
  periodLabel,
  relativeTime
} from '@/components/anomalies/humanize';
import {
  arrowGlyph,
  headlineFigure,
  magnitudeText,
  read as readAlert,
  trendBg,
  trendBorder,
  trendColor
} from '@/components/anomalies/presentation';
import { Header } from '@/components/header';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { SidebarProvider } from '@/components/ui/sidebar';
import { Textarea } from '@/components/ui/textarea';

import { useFeatures } from '@/hooks/useFeatures';

// ── Filter state ─────────────────────────────────────────────────────────────

type StatusFilter = 'OPEN' | 'RESOLVED' | 'ALL';
type TimeframeFilter = Timeframe | 'ALL';
type SeverityFilter = AnomalySeverity | 'ALL';

interface Filters {
  status: StatusFilter;
  timeframe: TimeframeFilter;
  severity: SeverityFilter;
}

const DEFAULT_FILTERS: Filters = {
  status: 'OPEN',
  timeframe: 'ALL',
  severity: 'ALL'
};

const STATUS_QUERY_MAP: Record<StatusFilter, string | undefined> = {
  OPEN: 'NEW,SEEN',
  RESOLVED: 'ACKNOWLEDGED,DISMISSED',
  ALL: undefined
};

const SEVERITY_STYLE: Record<AnomalySeverity, string> = {
  LOW: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700',
  MEDIUM:
    'bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-200 dark:border-sky-800',
  HIGH: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800',
  CRITICAL:
    'bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-200 dark:border-red-800'
};

// ── Page ─────────────────────────────────────────────────────────────────────

export default function Anomalies() {
  const navigate = useNavigate();
  const { features, loading: featuresLoading } = useFeatures();
  // Band headings are rendered here; the card picks its own sentence language
  // from the same source.
  const { i18n } = useTranslation();

  const [items, setItems] = useState<Anomaly[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<AnomalySummary | null>(null);

  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [detail, setDetail] = useState<Anomaly | null>(null);
  const [notesDraft, setNotesDraft] = useState<string>('');
  // The owner's weekly reading is what this page opens on. The full list is a
  // click away and unchanged — see WeeklyDigest for why the default flipped.
  const [ownerView, setOwnerView] = useState(true);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);

  const isAdmin = !featuresLoading && !!features.anomaly_admin;
  // Gated on the feature flags, NOT on isAdmin — creating an alert is the
  // thing every user is supposed to be able to do.
  //
  // Both flags, matching the backend's own gate (anomaly_alerts_api._gate).
  // Checking only anomaly_user_alerts here would show a button that 403s on
  // every call, and would disagree with the chat entry point.
  const canCreateAlerts =
    !featuresLoading &&
    !!features.anomaly_detection &&
    !!features.anomaly_user_alerts;

  useEffect(() => {
    if (featuresLoading) return;
    if (!features.anomaly_detection) navigate('/', { replace: true });
  }, [featuresLoading, features.anomaly_detection, navigate]);

  // ?anomaly=<id> opens that finding directly. The dashboard health panel links
  // here, and a link that only lands on the list leaves the reader hunting for
  // the row that cost the points — which is the moment they stop clicking.
  //
  // Fetched rather than found in `items`: the finding may sit on a page the
  // list has not loaded, or be filtered out entirely by the default OPEN
  // filter. The param is consumed once and then cleared, so a refresh or a
  // back-navigation does not reopen a sheet the reader already closed.
  const [deepLinked, setDeepLinked] = useState(false);
  useEffect(() => {
    if (featuresLoading || !features.anomaly_detection || deepLinked) return;
    const id = Number(
      new URLSearchParams(window.location.search).get('anomaly')
    );
    if (!Number.isInteger(id) || id <= 0) return;
    setDeepLinked(true);
    void (async () => {
      try {
        const fresh = await apiGetAnomaly(id);
        setDetail(fresh);
        setNotesDraft(fresh.review_notes ?? '');
      } catch {
        /* a stale or foreign id just leaves the list as it was */
      } finally {
        navigate('/anomalies', { replace: true });
      }
    })();
  }, [featuresLoading, features.anomaly_detection, deepLinked, navigate]);

  // Five cards for one story is how the first 75 alerts got ignored: sobhy's
  // purchases alerted on the 24th, the 25th, the 26th, that week AND that month,
  // every one of them driven by the same branch. Findings on the same metric are
  // collapsed into one card — the strongest one leads, the rest stay one click
  // away — so the page shows how many THINGS are happening, not how many rows
  // the check wrote.
  const groups = useMemo(() => {
    const visible =
      filters.status === 'OPEN'
        ? items.filter((a) => a.status === 'NEW' || a.status === 'SEEN')
        : items;
    const byMetric = new Map<string, Anomaly[]>();
    for (const a of visible) {
      const key = a.rule?.metric_name ?? `rule-${a.rule_id}`;
      const bucket = byMetric.get(key);
      if (bucket) bucket.push(a);
      else byMetric.set(key, [a]);
    }
    return [...byMetric.values()].map((list) => ({
      head: list[0],
      others: list.slice(1)
    }));
  }, [items, filters.status]);

  // Grouped by what they are worth, so the page reads as "here is the expensive
  // pile, here is the cheap one" rather than as one flat list the reader has to
  // rank themselves. The bands come from the server — the same cut-offs the
  // executor uses to cap severity — so a card cannot be filed under a heading
  // that contradicts its own grade.
  const banded = useMemo(
    () => groupByImpact(groups, (g) => impactBand(g.head)),
    [groups]
  );

  const listParams = useMemo(
    () => ({
      status: STATUS_QUERY_MAP[filters.status],
      timeframe: filters.timeframe === 'ALL' ? undefined : filters.timeframe,
      severity: filters.severity === 'ALL' ? undefined : filters.severity,
      limit: 50,
      // The expensive findings first. Severity is still in the ordering, as
      // the tie-break, but it no longer decides the top of the page: a
      // CRITICAL worth 0.2% of a month should not outrank a HIGH worth a
      // tenth.
      sort: 'impact' as const
    }),
    [filters]
  );

  const load = useCallback(
    async (append: boolean, next?: string | null) => {
      setLoading(true);
      setError(null);
      try {
        const [listRes, sumRes] = await Promise.all([
          apiListAnomalies({ ...listParams, cursor: next ?? undefined }),
          !append
            ? apiAnomalySummary()
            : Promise.resolve<AnomalySummary | null>(null)
        ]);
        // Taken in the order the server sent them. This used to re-sort every
        // page by severity on arrival, which is broken under keyset
        // pagination: each page was sorted in isolation and then appended, so
        // a LOW from page 1 sat above a CRITICAL from page 2. The ordering now
        // belongs to the query that produced the cursor.
        setItems((prev) =>
          append ? [...prev, ...listRes.items] : listRes.items
        );
        setCursor(listRes.next_cursor);
        if (sumRes) setSummary(sumRes);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    },
    [listParams]
  );

  useEffect(() => {
    void load(false, null);
  }, [load]);

  const openDetail = useCallback(async (a: Anomaly) => {
    setDetail(a);
    setNotesDraft(a.review_notes ?? '');
    try {
      const fresh = await apiGetAnomaly(a.id);
      // Keep the grade the card was showing. /anomalies/{id} returns the
      // detector's stored severity, so opening a card graded MEDIUM by money
      // put a LOW badge in the sheet — the same movement, two labels, one
      // click apart.
      setDetail({ ...fresh, severity: a.severity });
      setNotesDraft(fresh.review_notes ?? '');
      setItems((prev) =>
        prev.map((x) =>
          x.id === fresh.id
            ? {
                ...x,
                status: fresh.status,
                reviewer: fresh.reviewer,
                reviewed_at: fresh.reviewed_at
              }
            : x
        )
      );
    } catch {
      /* keep row-level */
    }
  }, []);

  const applyStatus = useCallback(
    async (status: AnomalyStatus) => {
      if (!detail) return;
      try {
        await apiPatchAnomaly(detail.id, {
          status,
          review_notes: notesDraft || undefined
        });
        setDetail({ ...detail, status, review_notes: notesDraft });
        setItems((prev) =>
          prev.map((x) =>
            x.id === detail.id ? { ...x, status, review_notes: notesDraft } : x
          )
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [detail, notesDraft]
  );

  // 13 findings each needing the drawer opened and closed is why 70 of the
  // first 75 were never touched. One click clears the whole story — the card
  // and every other period of the same metric behind it.
  const acknowledgeGroup = useCallback(
    async (list: Anomaly[]) => {
      const ids = list
        .filter((a) => a.status === 'NEW' || a.status === 'SEEN')
        .map((a) => a.id);
      if (ids.length === 0) return;
      // Optimistic: the OPEN filter drops them immediately, which is the whole
      // point of the button; a failure puts them back with the error shown.
      const before = items;
      setItems((prev) =>
        prev.map((x) =>
          ids.includes(x.id)
            ? { ...x, status: 'ACKNOWLEDGED' as AnomalyStatus }
            : x
        )
      );
      try {
        await Promise.all(
          ids.map((id) => apiPatchAnomaly(id, { status: 'ACKNOWLEDGED' }))
        );
        const sumRes = await apiAnomalySummary();
        setSummary(sumRes);
      } catch (e) {
        setItems(before);
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [items]
  );

  const saveNotesOnly = useCallback(async () => {
    if (!detail) return;
    try {
      await apiPatchAnomaly(detail.id, { review_notes: notesDraft });
      setDetail({ ...detail, review_notes: notesDraft });
      setItems((prev) =>
        prev.map((x) =>
          x.id === detail.id ? { ...x, review_notes: notesDraft } : x
        )
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [detail, notesDraft]);

  return (
    <SidebarProvider>
      <div className="flex flex-col h-screen w-full">
        <Header />

        <div className="flex-1 overflow-auto">
          <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">
            {/* Title + admin action */}
            <div className="flex items-end justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">
                  Anomalies
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {ownerView
                    ? 'What moved enough to matter, by week.'
                    : 'Unusual changes in your business metrics. Reviewed daily.'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* Two readings of the same record, said out loud. It was a
                    link at the very bottom of the page, under everything, and
                    nobody knew there was a second view at all. */}
                <div
                  className="flex items-center p-0.5 rounded-lg"
                  style={{ background: 'var(--sig-tab-active)' }}
                >
                  {(
                    [
                      ['weekly', 'Weekly'],
                      ['all', 'All']
                    ] as const
                  ).map(([key, label]) => {
                    const active = (key === 'weekly') === ownerView;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setOwnerView(key === 'weekly')}
                        className="text-[13px] font-medium rounded-[7px] px-3 py-1.5 transition-colors"
                        style={{
                          background: active
                            ? 'var(--sig-surface)'
                            : 'transparent',
                          color: active ? 'var(--sig-ink)' : 'var(--sig-muted)',
                          boxShadow: active
                            ? 'var(--sig-shadow-composer)'
                            : undefined
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                {canCreateAlerts ? (
                  <Button size="sm" onClick={() => setAlertsOpen(true)}>
                    <BellRing className="size-4 mr-2" />
                    My alerts
                  </Button>
                ) : null}
                {isAdmin ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRulesOpen(true)}
                  >
                    <Settings2 className="size-4 mr-2" />
                    Manage rules
                  </Button>
                ) : null}
              </div>
            </div>

            {/* The owner's week. The page below it is unchanged and one click
                away — this reduces what is shown, it does not remove anything.
                Reviewed DAILY by the detector, read WEEKLY by the person: a
                metric that moves five days running is one story, and thirty
                cards a day is a feed nobody triages. */}
            {ownerView ? (
              <WeeklyDigest
                onShowAll={() => setOwnerView(false)}
                // The page's own card, not a new one: it reads the alert's
                // meaning from the server, opens the detail sheet and carries
                // "Ask Signals why". Grouping by week must not cost any of them.
                renderItem={(item, others) => (
                  <AnomalyCard
                    anomaly={item}
                    others={others}
                    onOpen={() => void openDetail(item)}
                    onOpenOther={(a) => void openDetail(a)}
                    onAcknowledge={() =>
                      void acknowledgeGroup([item, ...others])
                    }
                  />
                )}
              />
            ) : (
              <>
                {/* Summary strip */}
                <SummaryStrip
                  summary={summary}
                  loading={loading && !summary}
                  storyCount={groups.length}
                  biggest={groups[0]?.head ?? null}
                />

                {/* Filter chip bar */}
                <FilterBar filters={filters} setFilters={setFilters} />

                {error ? (
                  <Card className="p-3 border-red-500 border text-sm text-red-700 dark:text-red-300">
                    {error}
                  </Card>
                ) : null}

                {/* List. A failed request must never be dressed as good news: the
                page used to show "HTTP 500" and "Nothing to worry about"
                together, which reads as an all-clear the data never gave. */}
                {items.length === 0 && !loading ? (
                  error ? (
                    <Card className="p-10 flex flex-col items-center text-center gap-2">
                      <div className="size-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                        <AlertTriangle className="size-6 text-amber-600 dark:text-amber-300" />
                      </div>
                      <div className="text-lg font-medium">
                        Could not load your findings
                      </div>
                      <div className="text-sm text-muted-foreground max-w-md">
                        This is a problem reaching Signals, not an all-clear —
                        your metrics have not been checked in this view. Try
                        again in a moment.
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => void load(false)}
                      >
                        Try again
                      </Button>
                    </Card>
                  ) : (
                    <EmptyState
                      filters={filters}
                      onReset={() => setFilters(DEFAULT_FILTERS)}
                    />
                  )
                ) : (
                  <div className="space-y-3">
                    {/* Grouped by what each finding is worth, so the full list
                        reads as "here is the expensive pile, here is the cheap
                        one" rather than as one flat run the reader has to rank
                        themselves. The weekly digest above organises by time
                        instead, which is a different question and deliberately
                        left alone. */}
                    {banded.map((bucket) => (
                      <div key={bucket.band} className="space-y-3">
                        <div className="flex items-center gap-2 pt-1">
                          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {impactBandLabel(
                              bucket.items[0].head,
                              i18n.language
                            )}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            · {bucket.items.length}
                          </span>
                          <div className="h-px flex-1 bg-border" />
                        </div>
                        {bucket.items.map((g) => (
                          <AnomalyCard
                            key={g.head.id}
                            anomaly={g.head}
                            others={g.others}
                            onOpen={() => void openDetail(g.head)}
                            onOpenOther={(a) => void openDetail(a)}
                            onAcknowledge={() =>
                              void acknowledgeGroup([g.head, ...g.others])
                            }
                          />
                        ))}
                      </div>
                    ))}
                    {loading ? (
                      <div className="text-center text-sm text-muted-foreground py-4">
                        Loading…
                      </div>
                    ) : null}
                    {cursor ? (
                      <div className="flex justify-center pt-2">
                        <Button
                          variant="outline"
                          onClick={() => void load(true, cursor)}
                          disabled={loading}
                        >
                          {loading ? 'Loading…' : 'Load more'}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {isAdmin ? (
          <RulesManager open={rulesOpen} onOpenChange={setRulesOpen} />
        ) : null}

        {canCreateAlerts ? (
          <AlertsDialog open={alertsOpen} onOpenChange={setAlertsOpen} />
        ) : null}

        <Sheet
          open={!!detail}
          onOpenChange={(o) => {
            if (!o) setDetail(null);
          }}
        >
          <SheetContent
            side="right"
            className="w-full sm:max-w-lg overflow-y-auto"
          >
            {detail ? (
              <AnomalyDetail
                anomaly={detail}
                isAdmin={isAdmin}
                notesDraft={notesDraft}
                setNotesDraft={setNotesDraft}
                onApplyStatus={applyStatus}
                onSaveNotes={saveNotesOnly}
              />
            ) : null}
          </SheetContent>
        </Sheet>
      </div>
    </SidebarProvider>
  );
}

// ── Summary strip ────────────────────────────────────────────────────────────

function SummaryStrip({
  summary,
  loading,
  storyCount,
  biggest
}: {
  summary: AnomalySummary | null;
  loading: boolean;
  storyCount: number;
  biggest: Anomaly | null;
}) {
  const open = summary?.open_count ?? 0;
  const critical = summary?.critical_open_count ?? 0;
  const highOpen = summary?.high_open_count ?? 0;
  const resolved = summary?.resolved_count ?? 0;
  const lastSeen = summary?.last_detected_at;

  const severityHint =
    critical > 0
      ? `${critical} critical`
      : highOpen > 0
        ? `${highOpen} high`
        : undefined;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <SummaryCard
        icon={
          <AlertTriangle
            className={`size-5 ${open > 0 ? 'text-red-500' : 'text-muted-foreground'}`}
          />
        }
        label="Need attention"
        value={loading ? '…' : String(open)}
        hint={
          open === 0
            ? 'All clear'
            : storyCount > 0 && storyCount < open
              ? `across ${storyCount} metrics${severityHint ? ` · ${severityHint}` : ''}`
              : severityHint
        }
        emphasize={open > 0}
      />
      {/* This tile used to repeat the critical count that the first tile already
          hints at, and on a healthy tenant it just said "0". What an owner wants
          in that space is WHICH one is worst. */}
      <SummaryCard
        icon={<Sparkles className="size-5 text-red-500" />}
        label="Biggest right now"
        value={loading ? '…' : biggest ? headlineFigure(biggest) : '—'}
        hint={
          biggest ? humanizeName(biggest.rule?.metric_name ?? '') : undefined
        }
      />
      <SummaryCard
        icon={<Check className="size-5 text-emerald-500" />}
        label="Resolved"
        value={loading ? '…' : String(resolved)}
      />
      <SummaryCard
        icon={<Clock className="size-5 text-muted-foreground" />}
        label="Last spotted"
        value={loading ? '…' : lastSeen ? relativeTime(lastSeen) : '—'}
        hint={lastSeen ? absoluteTime(lastSeen) : undefined}
      />
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  hint,
  emphasize
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  emphasize?: boolean;
}) {
  return (
    <Card
      className={`p-4 ${emphasize ? 'border-red-300 dark:border-red-800' : ''}`}
    >
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {hint ? (
        <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>
      ) : null}
    </Card>
  );
}

// ── Filter bar (chips) ───────────────────────────────────────────────────────

function FilterBar({
  filters,
  setFilters
}: {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <ChipGroup
        label="Show"
        options={[
          { key: 'OPEN', label: 'Need attention' },
          { key: 'RESOLVED', label: 'Resolved' },
          { key: 'ALL', label: 'All' }
        ]}
        value={filters.status}
        onChange={(v) =>
          setFilters((f) => ({ ...f, status: v as StatusFilter }))
        }
      />
      <div className="h-6 w-px bg-border" />
      <ChipGroup
        label="Severity"
        options={[
          { key: 'ALL', label: 'Any' },
          { key: 'CRITICAL', label: 'Critical' },
          { key: 'HIGH', label: 'High' },
          { key: 'MEDIUM', label: 'Medium' },
          { key: 'LOW', label: 'Low' }
        ]}
        value={filters.severity}
        onChange={(v) =>
          setFilters((f) => ({ ...f, severity: v as SeverityFilter }))
        }
      />
      <div className="h-6 w-px bg-border" />
      <ChipGroup
        label="Period"
        options={[
          { key: 'ALL', label: 'Any' },
          { key: 'DAY', label: 'Daily' },
          { key: 'WEEK', label: 'Weekly' },
          { key: 'MONTH', label: 'Monthly' },
          { key: 'YEAR', label: 'Yearly' }
        ]}
        value={filters.timeframe}
        onChange={(v) =>
          setFilters((f) => ({ ...f, timeframe: v as TimeframeFilter }))
        }
      />
    </div>
  );
}

function ChipGroup<T extends string>({
  label,
  options,
  value,
  onChange
}: {
  label: string;
  options: { key: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <div className="flex flex-wrap gap-1">
        {options.map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.key)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
              value === opt.key
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background text-foreground/70 hover:bg-muted border-border'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({
  filters,
  onReset
}: {
  filters: Filters;
  onReset: () => void;
}) {
  const isDefaultOpen =
    filters.status === 'OPEN' &&
    filters.severity === 'ALL' &&
    filters.timeframe === 'ALL';
  return (
    <Card className="p-10 flex flex-col items-center text-center gap-2">
      <div className="size-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
        <Check className="size-6 text-emerald-600 dark:text-emerald-300" />
      </div>
      <div className="text-lg font-medium">
        {isDefaultOpen ? 'Nothing to worry about' : 'No matches'}
      </div>
      <div className="text-sm text-muted-foreground max-w-md">
        {isDefaultOpen
          ? 'No unusual changes need your attention right now. We check daily and will flag anything out of pattern.'
          : "No anomalies match the filters you've picked."}
      </div>
      {!isDefaultOpen ? (
        <Button variant="outline" size="sm" className="mt-2" onClick={onReset}>
          Clear filters
        </Button>
      ) : null}
    </Card>
  );
}

// ── Anomaly card ─────────────────────────────────────────────────────────────

function AnomalyCard({
  anomaly,
  others = [],
  onOpen,
  onOpenOther,
  onAcknowledge
}: {
  anomaly: Anomaly;
  others?: Anomaly[];
  onOpen: () => void;
  onOpenOther?: (a: Anomaly) => void;
  onAcknowledge?: () => void;
}) {
  const navigate = useNavigate();
  // "Ask Signals why" has to open a NEW conversation, and only
  // clear() makes that true — see the comment on the button below.
  const { clear } = useChatInteract();
  const setAnomalyToExplain = useSetRecoilState(anomalyToExplainState);
  const [othersOpen, setOthersOpen] = useState(false);
  // How this reads is decided on the server, by the same function that writes
  // the alert title and the context sent to the model. `direction` is the
  // rule's watch filter, not what happened, and reading it here is how returns
  // falling to zero got an up arrow over "-100%".
  const told = readAlert(anomaly);
  const isUnresolved = anomaly.status === 'NEW' || anomaly.status === 'SEEN';
  // Both languages arrive on the alert; this only picks one. The money itself
  // is never computed here.
  const { i18n } = useTranslation();
  const worth = impactText(anomaly, i18n.language);
  const worthMeasured = impactScaleKnown(anomaly);

  const colour = trendColor(told.arrow);
  const background = trendBg(told.arrow);
  const TrendIcon =
    told.arrow === 'down'
      ? ArrowDownRight
      : told.arrow === 'up'
        ? ArrowUpRight
        : Minus;

  const name = humanizeName(
    anomaly.rule?.metric_name ?? `metric #${anomaly.rule_id}`
  );
  const timeframeLower = comparisonLabel(anomaly).toLowerCase();
  const yoy = isYearOverYear(anomaly);
  const notes = comparisonNotes(anomaly);

  return (
    <button onClick={onOpen} className="w-full text-left">
      <Card
        className={`p-5 hover:shadow-md transition-shadow ${!isUnresolved ? 'opacity-70' : ''}`}
      >
        <div className="flex items-start gap-4">
          {/* Trend icon block */}
          <div
            className={`shrink-0 size-12 rounded-lg flex items-center justify-center ${background}`}
          >
            <TrendIcon className={`size-6 ${colour}`} />
          </div>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-semibold truncate text-base">{name}</span>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium border ${SEVERITY_STYLE[anomaly.severity]}`}
              >
                {anomaly.severity.charAt(0) +
                  anomaly.severity.slice(1).toLowerCase()}
              </span>
              {isUnresolved ? null : (
                <span className="text-xs text-muted-foreground">
                  · {anomaly.status.toLowerCase()}
                </span>
              )}
            </div>

            {/* Headline sentence: "Down 45% day-over-day". For a balance there
                is no percentage, so it reads "Shifted toward credit 939.96". */}
            <div className="mt-1.5 flex items-baseline gap-2 flex-wrap">
              <span className={`text-lg font-semibold ${colour}`}>
                {told.verb} {magnitudeText(told)}
              </span>
              <span className="text-sm text-muted-foreground">
                {timeframeLower}
              </span>
            </div>

            {/* From → To in plain language, and WHICH days. "Spotted 20 hours
                ago" is when the check ran, not when the business moved — the
                card used to show only that. */}
            <div className="mt-1 text-sm tabular-nums">
              <span className="text-muted-foreground">from </span>
              <span className="font-medium">
                {formatValue(told.priorValue)}
              </span>
              <span className="text-muted-foreground"> to </span>
              <span className={`font-medium ${colour}`}>
                {formatValue(told.currentValue)}
              </span>
              {told.absoluteChange !== null ? (
                <span className="text-muted-foreground">
                  {' '}
                  · a change of {formatValue(Math.abs(told.absoluteChange))}
                </span>
              ) : null}
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {periodLabel(
                anomaly.current_period_start,
                anomaly.current_period_end,
                anomaly.timeframe,
                yoy
              )}
              {' vs '}
              {periodLabel(
                anomaly.prior_period_start,
                anomaly.prior_period_end,
                anomaly.timeframe,
                yoy
              )}
            </div>
            {/* "Last year this date was Eid al-Adha." A same-date comparison
                is only honest when it says which side was a holiday. */}
            {notes.length ? (
              <div className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                {notes.join(' ')}
              </div>
            ) : null}

            {/* What it is WORTH. A percentage says how far out of pattern the
                movement is; this says whether it is worth the owner's morning,
                and it is the line that turns a finding into a decision.

                The whole sentence comes from the server — amount, share of a
                normal month, threshold crossed, run rate — because the rules
                for building it (a total scales differently from an average, a
                metric with no monthly scale gets no number at all) have to hold
                identically here, in the dashboard narrative and in what the
                model is told. This used to be assembled inline from raw context
                and had already drifted: it read the AVG case off the driver as
                well as off the aggregate, and said nothing whatsoever when the
                scale was unknown, which left a finding looking unimportant
                rather than unmeasured. */}
            {worth ? (
              <div
                className={`mt-2 text-sm leading-snug ${
                  worthMeasured ? 'font-medium' : 'italic text-muted-foreground'
                }`}
              >
                {worth}
              </div>
            ) : null}

            {/* A metric that came back to where it was is not a new problem. */}
            {(anomaly.context as any)?.reversal_of ? (
              <div className="mt-2 text-xs inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-muted-foreground">
                ↩ settled back to its earlier level
              </div>
            ) : null}

            {/* What actually moved — computed at detection time, not guessed */}
            {(() => {
              const d = (anomaly.context as any)?.driver;
              if (!d || !d.value) return null;
              const sign = d.delta > 0 ? '+' : '';
              const measured = driverShareIsTrustworthy(d, anomaly.delta);
              const offset = driverOffsetByOthers(d);
              const quotable = measured && !offset;
              const isAverage = d.kind === 'average';
              return (
                <div className="mt-2 text-sm">
                  <span className="text-muted-foreground">
                    {quotable
                      ? isAverage
                        ? 'Moved mainly by '
                        : 'Driven mainly by '
                      : 'Largest mover: '}
                  </span>
                  <span className="font-medium" dir="auto">
                    {d.value}
                  </span>
                  <span className="text-muted-foreground">
                    {/* When the driver's arithmetic sits on a different quantity
                        than the finding's own delta, its absolute number is just
                        as confusing as its share ("+2.37M" beside "a change of
                        95.03K"), so the mover is named and nothing is claimed. */}
                    {quotable
                      ? ` (${sign}${formatValue(d.delta)} — ${Math.round(d.share_pct)}% of the change)`
                      : measured
                        ? ` (${sign}${formatValue(d.delta)}) — more than the net change, because others moved the opposite way`
                        : ''}
                  </span>
                  {isAverage && d.group_current_avg != null ? (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      its own average went from {formatValue(d.group_prior_avg)}{' '}
                      to {formatValue(d.group_current_avg)}
                      {d.group_rows_current != null
                        ? ` on ${d.group_rows_current} line${d.group_rows_current === 1 ? '' : 's'}`
                        : ''}
                      {d.group_rows_prior != null
                        ? ` (was ${d.group_rows_prior})`
                        : ''}
                    </div>
                  ) : null}
                </div>
              );
            })()}

            {/* Hand the finding to the chat, which can actually dig */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Only the ID travels. The sentence the model is given is built
                // on the server from the alert row (anomaly_context.build_context),
                // because this code used to build it here and got it wrong: it read
                // `direction` -- which is the rule's FILTER, not what happened -- so
                // a 1055% rise was sent as a fall, and it printed the exclusive
                // period end verbatim, making one day read as two.
                setAnomalyToExplain(String(anomaly.id));
                // Same tab, not a new one: every chat tab reserves its own
                // container, and the owner came here for an answer, not to
                // collect windows. clear() is what makes it a NEW conversation --
                // without it Recoil still holds the previous threadId and the chat
                // redirects straight back to it, which is how a question about one
                // finding ended up appended to another finding's conversation.
                clear();
                navigate('/');
              }}
              className="mt-2 text-xs rounded-full border px-3 py-1 text-muted-foreground hover:bg-muted transition-colors"
            >
              💬 Ask Signals why
            </button>

            {onAcknowledge &&
            (anomaly.status === 'NEW' || anomaly.status === 'SEEN') ? (
              <span
                role="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onAcknowledge();
                }}
                className="mt-2 ml-2 inline-flex items-center gap-1 text-xs rounded-full border px-3 py-1 text-muted-foreground hover:bg-muted transition-colors"
              >
                <Check className="size-3" />
                {others.length > 0
                  ? `Reviewed (${others.length + 1})`
                  : 'Reviewed'}
              </span>
            ) : null}

            {/* Business description */}
            {anomaly.rule?.notes ? (
              <div className="mt-2 text-sm text-foreground/80 leading-snug">
                {anomaly.rule.notes}
              </div>
            ) : null}

            <div className="mt-2 text-xs text-muted-foreground">
              Spotted {relativeTime(anomaly.detected_at)}
            </div>

            {/* Other periods where the same metric moved — the rest of the story */}
            {others.length > 0 ? (
              <div className="mt-3 border-t pt-2">
                <span
                  role="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOthersOpen((o) => !o);
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                >
                  <span>{othersOpen ? '▾' : '▸'}</span>
                  {others.length} more period{others.length > 1 ? 's' : ''}{' '}
                  where this moved
                </span>
                {othersOpen ? (
                  <div className="mt-2 space-y-1">
                    {others.map((o) => (
                      <span
                        key={o.id}
                        role="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenOther?.(o);
                        }}
                        className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground py-0.5"
                      >
                        <span
                          className={`px-1.5 rounded-full border text-[10px] ${SEVERITY_STYLE[o.severity]}`}
                        >
                          {o.severity.charAt(0) +
                            o.severity.slice(1).toLowerCase()}
                        </span>
                        <span className="tabular-nums">
                          {headlineFigure(o)}
                        </span>
                        <span>{comparisonLabel(o).toLowerCase()}</span>
                        <span>
                          ·{' '}
                          {periodLabel(
                            o.current_period_start,
                            o.current_period_end,
                            o.timeframe
                          )}
                        </span>
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </Card>
    </button>
  );
}

// ── Detail drawer ────────────────────────────────────────────────────────────

function AnomalyDetail({
  anomaly,
  isAdmin,
  notesDraft,
  setNotesDraft,
  onApplyStatus,
  onSaveNotes
}: {
  anomaly: Anomaly;
  isAdmin: boolean;
  notesDraft: string;
  setNotesDraft: (v: string) => void;
  onApplyStatus: (s: AnomalyStatus) => void;
  onSaveNotes: () => void;
}) {
  const [techOpen, setTechOpen] = useState(false);
  // Same server-side reading as the card. The sheet and the card sitting behind
  // it disagreeing about which way a metric moved would be its own kind of bug.
  const told = readAlert(anomaly);
  const colour = trendColor(told.arrow);
  const TrendIcon =
    told.arrow === 'down'
      ? ArrowDownRight
      : told.arrow === 'up'
        ? ArrowUpRight
        : Minus;
  const name = humanizeName(
    anomaly.rule?.metric_name ?? `Rule #${anomaly.rule_id}`
  );
  const zScore =
    anomaly.context && typeof (anomaly.context as any).z_score === 'number'
      ? ((anomaly.context as any).z_score as number)
      : null;

  return (
    <>
      <SheetHeader>
        <SheetTitle className="text-xl">{name}</SheetTitle>
        <SheetDescription>
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium border ${SEVERITY_STYLE[anomaly.severity]}`}
          >
            {anomaly.severity.charAt(0) +
              anomaly.severity.slice(1).toLowerCase()}
          </span>
          {' · '}
          <span>{comparisonLabel(anomaly)}</span>
          {' · '}
          <span>{relativeTime(anomaly.detected_at)}</span>
        </SheetDescription>
      </SheetHeader>

      {/* Hero */}
      <div className="mt-6 flex items-center gap-4">
        <div
          className={`size-14 rounded-lg flex items-center justify-center ${trendBg(told.arrow)}`}
        >
          <TrendIcon className={`size-8 ${colour}`} />
        </div>
        <div>
          <div className={`text-3xl font-semibold tabular-nums ${colour}`}>
            {arrowGlyph(told.arrow)} {magnitudeText(told)}
          </div>
          <div className="text-sm text-muted-foreground">
            {told.verb} {comparedWith(anomaly)}
          </div>
        </div>
      </div>

      {/* Comparison */}
      <div className="mt-6 grid grid-cols-2 gap-3">
        <Card className="p-4">
          <div className="text-xs uppercase text-muted-foreground tracking-wide">
            Prior
          </div>
          <div className="text-2xl font-semibold tabular-nums mt-1">
            {formatValue(told.priorValue)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {periodLabel(
              anomaly.prior_period_start,
              anomaly.prior_period_end,
              anomaly.timeframe,
              isYearOverYear(anomaly)
            )}
          </div>
        </Card>
        <Card className={`p-4 border-2 ${trendBorder(told.arrow)}`}>
          <div className="text-xs uppercase text-muted-foreground tracking-wide">
            Current
          </div>
          <div className={`text-2xl font-semibold tabular-nums mt-1 ${colour}`}>
            {formatValue(told.currentValue)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {periodLabel(
              anomaly.current_period_start,
              anomaly.current_period_end,
              anomaly.timeframe,
              isYearOverYear(anomaly)
            )}
          </div>
        </Card>
      </div>

      {comparisonNotes(anomaly).length ? (
        <div className="mt-3 rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          {comparisonNotes(anomaly).map((n) => (
            <div key={n}>{n}</div>
          ))}
        </div>
      ) : null}

      {/* Z-score note (for statistical rules) */}
      {zScore !== null ? (
        <div className="mt-4 text-sm text-muted-foreground">
          Statistical deviation:{' '}
          <span className="font-mono text-foreground">
            {zScore.toFixed(2)}σ
          </span>{' '}
          from a baseline of {(anomaly.context as any).baseline_samples ?? '—'}{' '}
          similar periods.
        </div>
      ) : null}

      {/* Rule notes */}
      {anomaly.rule?.notes ? (
        <div className="mt-4 p-3 rounded-md bg-muted/40 text-sm">
          <div className="text-xs text-muted-foreground mb-1">
            About this rule
          </div>
          {anomaly.rule.notes}
        </div>
      ) : null}

      {/* Review area */}
      <div className="mt-6 border-t pt-4">
        <div className="text-sm font-medium mb-2">Review</div>
        <div className="text-xs text-muted-foreground mb-2">
          Status:{' '}
          <span className="font-medium text-foreground">
            {anomaly.status.charAt(0) + anomaly.status.slice(1).toLowerCase()}
          </span>
          {anomaly.reviewer ? (
            <>
              {' '}
              · by <span className="font-medium">{anomaly.reviewer}</span>
            </>
          ) : null}
          {anomaly.reviewed_at ? (
            <> · {relativeTime(anomaly.reviewed_at)}</>
          ) : null}
        </div>

        <Textarea
          value={notesDraft}
          onChange={(e) => setNotesDraft(e.target.value)}
          rows={3}
          placeholder="Add a note (optional)…"
        />
        <div className="flex justify-end mt-2">
          <Button variant="ghost" size="sm" onClick={onSaveNotes}>
            Save note
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-3">
          <Button
            variant="default"
            size="sm"
            onClick={() => onApplyStatus('ACKNOWLEDGED')}
          >
            <Check className="size-4 mr-1.5" />
            Acknowledge
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onApplyStatus('DISMISSED')}
          >
            <X className="size-4 mr-1.5" />
            Dismiss
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          <strong>Acknowledge</strong> if it's a real issue you're handling.
          <strong> Dismiss</strong> if it's a false alarm.
        </p>
      </div>

      {/* Technical details — admins only, collapsed by default */}
      {isAdmin ? (
        <div className="mt-6 border-t pt-4">
          <button
            type="button"
            onClick={() => setTechOpen((o) => !o)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronDown
              className={`size-4 transition-transform ${techOpen ? 'rotate-180' : ''}`}
            />
            Technical details
          </button>
          {techOpen ? (
            <div className="mt-3 space-y-2 text-xs">
              <TechRow k="Anomaly ID" v={String(anomaly.id)} />
              <TechRow k="Rule ID" v={String(anomaly.rule_id)} />
              <TechRow
                k="Table"
                v={<code className="text-xs">{anomaly.table_name}</code>}
              />
              {anomaly.rule?.rule_type ? (
                <TechRow k="Rule type" v={anomaly.rule.rule_type} />
              ) : null}
              <TechRow k="Direction" v={anomaly.direction} />
              <TechRow
                k="Delta"
                v={`${formatValue(anomaly.delta)} (${formatPct(anomaly.delta_pct, { signed: true })})`}
              />
              {anomaly.context ? (
                <details className="mt-2">
                  <summary className="cursor-pointer text-muted-foreground">
                    Rule context
                  </summary>
                  <pre className="mt-1 bg-muted p-2 rounded overflow-auto max-h-40 font-mono text-[11px]">
                    {JSON.stringify(anomaly.context, null, 2)}
                  </pre>
                </details>
              ) : null}
              {anomaly.rule?.metric_sql ? (
                <details>
                  <summary className="cursor-pointer text-muted-foreground">
                    metric_sql
                  </summary>
                  <pre className="mt-1 bg-muted p-2 rounded overflow-auto max-h-40 font-mono text-[11px]">
                    {anomaly.rule.metric_sql}
                  </pre>
                </details>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

function TechRow({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{k}</span>
      <span className="text-right">{v}</span>
    </div>
  );
}
